import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { z } from "zod";
import { Button } from "@/ui/Button";
import { fieldInputClass } from "@/ui/modals/Modal";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { useLogin } from "./auth.hooks";

/**
 * Mirrors the API `loginSchema` (apps/api/src/modules/auth/auth.schema.ts).
 * Messages are i18n keys, translated at render time.
 */
const schema = z.object({
  email: z.string().email("auth.errors.emailInvalid"),
  password: z.string().min(1, "auth.errors.passwordRequired"),
});

const labelClass = "block text-[12.5px] font-semibold text-text2";

const BRAND_STATS = [
  { value: "auth.brand.stats.tasksValue", label: "auth.brand.stats.tasksLabel" },
  { value: "auth.brand.stats.cashValue", label: "auth.brand.stats.cashLabel" },
  { value: "auth.brand.stats.deliveryValue", label: "auth.brand.stats.deliveryLabel" },
] as const;

function BrandMark({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex h-10 w-10 items-center justify-center rounded bg-primary text-white font-display font-bold text-lg shadow-card">
        F
      </div>
      <span className="font-display text-[13px] font-bold uppercase tracking-[0.16em] text-text">
        {t("auth.title")}
      </span>
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (login.isPending) return;
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const fieldErrors: { email?: string; password?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "email" || key === "password") fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    login.mutate(parsed.data, {
      onSuccess: () => {
        toast.success(t("auth.loginSuccess"));
        navigate("/dashboard");
      },
      onError: (err) => toast.error(getErrorMessage(err, t("auth.loginFailed"))),
    });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-bg">
      {/* Brand panel (desktop only) */}
      <aside className="relative hidden lg:flex flex-col overflow-hidden border-r border-border bg-bg2 px-10 py-10 xl:px-14">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-36 -right-36 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -left-28 h-[360px] w-[360px] rounded-full bg-violet/10 blur-3xl"
        />

        <BrandMark className="relative" />

        <div className="relative flex flex-1 flex-col justify-center max-w-[480px]">
          <h2 className="font-display text-[32px] xl:text-[36px] font-bold leading-[1.15] text-text">
            {t("auth.brand.tagline")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-text2">{t("auth.brand.taglineNote")}</p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {BRAND_STATS.map((stat) => (
              <div
                key={stat.label}
                className="rounded border border-border bg-card px-4 py-3.5 shadow-card"
              >
                <p className="font-display text-[20px] font-bold text-text">{t(stat.value)}</p>
                <p className="mt-0.5 text-[12px] leading-snug text-text3">{t(stat.label)}</p>
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex items-center justify-center px-4 py-10 sm:px-10">
        <div className="w-full max-w-[400px]">
          <div className="bg-card border border-border2 rounded-lg shadow-card px-7 py-8 sm:px-8">
            <div className="mb-7">
              <BrandMark className="lg:hidden mb-6" />
              <h1 className="font-display text-[22px] font-bold leading-tight text-text">
                {t("auth.welcomeBack")}
              </h1>
              <p className="mt-1 text-[13px] text-text3">{t("auth.subtitle")}</p>
            </div>

            <form onSubmit={submit} noValidate>
              {/* Email */}
              <div className="mb-4">
                <div className="mb-1.5">
                  <label htmlFor="login-email" className={labelClass}>
                    {t("auth.email")}
                  </label>
                </div>
                <input
                  id="login-email"
                  className={fieldInputClass}
                  type="email"
                  autoFocus
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="owner@fadagency.dz"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="mt-1.5 text-[12px] text-danger">{t(errors.email)}</p>
                )}
              </div>

              {/* Password */}
              <div className="mb-5">
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="login-password" className={labelClass}>
                    {t("auth.password")}
                  </label>
                  <button
                    type="button"
                    onClick={() => toast.info(t("auth.forgotPasswordHint"))}
                    className="text-[12px] font-medium text-text3 hover:text-primary transition-colors"
                  >
                    {t("auth.forgotPassword")}
                  </button>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    className={`${fieldInputClass} pr-10`}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    aria-invalid={!!errors.password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-text3 hover:text-text2 transition-colors"
                    aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-[12px] text-danger">{t(errors.password)}</p>
                )}
              </div>

              <Button type="submit" className="w-full" loading={login.isPending}>
                {login.isPending ? t("common.saving") : t("auth.submit")}
              </Button>
            </form>

            <p className="mt-5 flex items-center justify-center gap-1.5 text-[12px] text-text3">
              <ShieldCheck className="h-3.5 w-3.5 text-success" aria-hidden="true" />
              {t("auth.securityNote")}
            </p>
          </div>

          <p className="mt-5 text-center text-[12px] text-text3">{t("auth.noAccount")}</p>
        </div>
      </main>
    </div>
  );
}
